import { isValidUsageEventField } from '@monetize/constants';
import { v4 as uuidv4 } from 'uuid';

export type UsagePropertyRow = {
  id: string;
  key: string;
  value: string;
};

/**
 * Anything carrying a usage event `properties` map. Both the stored event and
 * the create/upload request shape qualify, so the property column helpers work
 * for saved tables and the import preview alike.
 */
export type UsagePropertyRecord = {
  properties?: Record<string, string> | null;
};

export function isValidUsagePropertyKey(value: string): boolean {
  return isValidUsageEventField(value);
}

export function propertiesObjectToRows(
  properties: Record<string, string> | null | undefined,
): UsagePropertyRow[] {
  if (!properties) {
    return [];
  }
  return Object.entries(properties).map(([key, value]) => ({
    id: uuidv4(),
    key,
    value,
  }));
}

export function propertyRowsToObject(
  rows: UsagePropertyRow[] | undefined,
): Record<string, string> | undefined {
  if (!rows || rows.length === 0) {
    return undefined;
  }
  const result: Record<string, string> = {};
  for (const { key, value } of rows) {
    const trimmedKey = key.trim();
    const trimmedValue = value.trim();
    if (!trimmedKey || !trimmedValue) {
      continue;
    }
    result[trimmedKey] = trimmedValue;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export function collectPropertyKeys(records: UsagePropertyRecord[]): string[] {
  const keys = new Set<string>();
  for (const record of records) {
    if (!record.properties) {
      continue;
    }
    for (const key of Object.keys(record.properties)) {
      keys.add(key);
    }
  }
  return [...keys].sort();
}

/**
 * Column keys for a usage table: every configured custom field first (in the
 * order given, which is the metadata order), then any key that only appears in
 * the loaded data — e.g. a property written before its field was configured, or
 * one whose field has since been removed — sorted, so nothing stored is hidden.
 */
export function mergePropertyColumnKeys(
  configuredKeys: string[],
  dataKeys: string[],
): string[] {
  const configured = new Set(configuredKeys);
  const dataOnly = dataKeys.filter((key) => !configured.has(key)).sort();
  return [...configured, ...dataOnly];
}

/**
 * Builds the `properties` payload from a metadata-driven form: one value per
 * configured key. Values are trimmed and empties dropped, since the usage
 * service treats `properties` as write-on-create and would persist blanks.
 */
export function propertyValuesToObject(
  values: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!values) {
    return undefined;
  }
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
      continue;
    }
    result[key] = trimmedValue;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}
